import { inject, Injectable } from '@angular/core';
import { AuthConfig, NullValidationHandler, OAuthService } from 'angular-oauth2-oidc';
import { Router, UrlTree } from '@angular/router';
import { catchError, EMPTY, from, map, Observable, of, ReplaySubject, tap } from 'rxjs';
import { ENV_CONFIG, EnvConfiguration } from '@cumlaude/shared-configuration';
import { BugsnagService } from '@cumlaude/bugsnag';
import { toSignal } from '@angular/core/rxjs-interop';

type Claims = 'name' | 'sub';

@Injectable({
	providedIn: 'root',
})
export class AuthService {
	private readonly bugsnag = inject(BugsnagService);
	private readonly envConfig = inject<EnvConfiguration>(ENV_CONFIG);
	private readonly oauthService = inject(OAuthService);
	private readonly router = inject(Router);

	loggedIn$ = new ReplaySubject<boolean>(1);

	readonly developer = toSignal(this.loggedIn$.pipe(map(() => this.isUserDeveloper())), { initialValue: false });

	sub$: Observable<string> = this.loggedIn$.pipe(
		map((loggedIn) => {
			return loggedIn ? this.oauthService.getIdentityClaims()['sub'] : '-';
		})
	);

	constructor() {
		this.initOAuthService();
		this.loggedIn$.next(this.isLoggedIn());
	}

	private get authServerURI() {
		return this.envConfig.authUrl;
	}

	private get oAuthEndpoint() {
		return this.authServerURI + '/oauth2';
	}

	public get requestedUrl() {
		return sessionStorage.getItem('requestedUrl');
	}

	public get rootUrlTree(): UrlTree {
		return this.router.parseUrl('/');
	}

	public get oauthUrlTree(): UrlTree {
		return this.router.parseUrl('/oauth');
	}

	private get authConfig() {
		return {
			clientId: this.envConfig.clientId,

			customQueryParams: {
				claims: '{ "id_token": { "name": null} }',
			},
			issuer: this.authServerURI,
			loginUrl: this.oAuthEndpoint + '/authorize',
			tokenEndpoint: this.oAuthEndpoint + '/token',
			logoutUrl: this.oAuthEndpoint + '/logout',
			redirectUri: this.callBackURI,
			scope: 'openid',
			oidc: true,
			postLogoutRedirectUri: this.postLogoutRedirectUri,
			responseType: 'code',
		} as AuthConfig;
	}

	private get callBackURI() {
		return this.protocol() + '//' + this.host() + '/oauth';
	}

	private get postLogoutRedirectUri() {
		return this.protocol() + '//' + this.host();
	}

	public storeRequestedUrl(currentUrl: string) {
		if (!currentUrl.includes('oauth')) {
			sessionStorage.setItem('requestedUrl', currentUrl);
		}
	}

	public isLoggedIn(): boolean {
		return this.oauthService.hasValidAccessToken() && this.oauthService.hasValidIdToken();
	}

	public getUserDisplayName(): string {
		const claims: Record<Claims, any> = this.oauthService.getIdentityClaims();
		return claims ? `${claims.name}` : '';
	}

	public isUserDeveloper(): boolean {
		const claims: Record<Claims, any> = this.oauthService.getIdentityClaims();
		return claims?.sub.startsWith('D/');
	}

	public clearSessionStorage() {
		const storage = sessionStorage;
		storage.removeItem('access_token');
		storage.removeItem('id_token');
		storage.removeItem('refresh_token');
		storage.removeItem('expires_at');
		storage.removeItem('id_token_claims_obj');
		storage.removeItem('id_token_expires_at');
		storage.removeItem('id_token_stored_at');
		storage.removeItem('access_token_stored_at');
	}

	public logoff() {
		this.oauthService.logOut();
	}

	public host() {
		return window.location.host;
	}

	public protocol() {
		return window.location.protocol;
	}

	public refreshTokenIfNeeded(): Observable<boolean> {
		if (this.isLoggedIn()) return of(true);

		this.loggedIn$.next(false);

		// Als er geen refresh token is kunnen we niks. Deze situatie komt voor als je op
		// /oauth/new aankomt terwijl er nog een oud access token aanwezig is.
		// Die wordt dan wel weggehaald maar er gaan nog allerhande requests af.
		// Doordat EMPTY afsluit zonder te emitten worden downstream requests niet afgevuurd.
		if (!this.oauthService.getRefreshToken()) return EMPTY;

		return from(this.oauthService.refreshToken()).pipe(
			map(() => true),
			tap(() => this.loggedIn$.next(true)),
			// idem voor als het token request faalt (bijv. expired/revoked)
			catchError(() => EMPTY)
		);
	}

	private initOAuthService() {
		this.oauthService.configure(this.authConfig);
		this.oauthService.tokenValidationHandler = new NullValidationHandler();
		this.oauthService.setupAutomaticSilentRefresh();
	}

	public async login(oauthParams: {}) {
		try {
			// als we vanaf de authenticator komen met een code: probeer een access token te krijgen
			await this.oauthService.tryLogin({ disableOAuth2StateCheck: true });

			if (this.isLoggedIn()) {
				this.loggedIn$.next(true);
				const info = this.oauthService.state ? { toastr: this.oauthService.state } : undefined;
				return await this.router.navigateByUrl(this.requestedUrl ?? this.rootUrlTree, { info });
			} else {
				// geen access token gekregen -> redirect naar authenticator voor een code
				this.oauthService.initCodeFlow(undefined, oauthParams);
				return true;
			}
		} catch (error) {
			const errorType = (error as { type?: string }).type ?? 'Unknown error';

			this.bugsnag.notify({
				name: 'authentication_error',
				message: `Authenticatie error ${errorType}`,
			});
			return false;
		}
	}
}
